/**
 * Task 3.8 — Assign-mode payload composer.
 *
 * Mirrors `RecurringAssigneesDrawer.handleSave` (L111-141) — emits
 * `OnSubmitReturnType = { apiData: { new, updated, deleted }, tableData }`.
 *
 * Behavioural notes:
 *  - Each `SelectedMemberEntry.timeSlots[]` entry produces ONE row in both
 *    `apiNew` and `tableData` (parity-extension over the recurring drawer to
 *    support multi-slot per member if assign mode ever supplies time data).
 *  - When `timeSlots.length === 0` (current default in assign mode — see
 *    `toggleSelection`), emit ONE row with `start_time: null, end_time: null`,
 *    matching the recurring drawer L111-141 contract exactly.
 *  - The downstream submit pipeline differentiates "new vs updated vs deleted"
 *    via the existing host transform; preserving the shape here is sufficient.
 */

import { v4 as uuidv4 } from "uuid";
import type {
  AssigneeData,
  AssigneeTableData,
  OnSubmitReturnType,
} from "../../type/assigneeType";
import type { SelectedMemberEntry } from "../types";

export const buildAssignPayload = (
  selectedMap: Map<string, SelectedMemberEntry>,
): OnSubmitReturnType => {
  const apiNew: AssigneeData[] = [];
  const tableData: AssigneeTableData[] = [];

  for (const entry of selectedMap.values()) {
    const { resource, timeSlots } = entry;

    // Recurring-drawer parity: a member with NO time slots emits exactly one
    // row with null start/end. Multi-slot members emit one row per slot.
    if (timeSlots.length === 0) {
      const rowUuid = uuidv4();
      apiNew.push({
        uuid: rowUuid,
        first_name: resource.first_name,
        last_name: resource.last_name,
        profile_image: resource.profile_image ?? "",
        resource_id: resource.mapping_id,
        schedule_times: [
          {
            schedule_uuid: rowUuid,
            mapping_id: null,
            start_time: null,
            end_time: null,
          },
        ],
      });
      tableData.push({
        uuid: rowUuid,
        resource_id: resource.mapping_id,
        first_name: resource.first_name,
        last_name: resource.last_name,
        profile_image: resource.profile_image ?? "",
        start_time: null,
        end_time: null,
        errors: { start_time: null, end_time: null },
      });
      continue;
    }

    for (const slot of timeSlots) {
      const rowUuid = uuidv4();
      apiNew.push({
        uuid: rowUuid,
        first_name: resource.first_name,
        last_name: resource.last_name,
        profile_image: resource.profile_image ?? "",
        resource_id: resource.mapping_id,
        schedule_times: [
          {
            schedule_uuid: rowUuid,
            mapping_id: slot.scheduleId,
            start_time: slot.startIso,
            end_time: slot.endIso,
          },
        ],
      });
      tableData.push({
        uuid: rowUuid,
        resource_id: resource.mapping_id,
        first_name: resource.first_name,
        last_name: resource.last_name,
        profile_image: resource.profile_image ?? "",
        start_time: slot.startIso,
        end_time: slot.endIso,
        errors: { start_time: null, end_time: null },
      });
    }
  }

  return {
    apiData: { new: apiNew, updated: [], deleted: [] },
    tableData,
  };
};
